import { toast, createLazyResource, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import initialCategories from '~/pages/maintenance-request/maintenance-request-categories/initialCategories';
import { maintenanceRequestCategoryRepository } from '~/repositories/categoryRepository';
import { MaintenanceRequestRepository } from '~/repositories/maintenanceRequestRepository';
import { getCachedPropertyManagers } from '~/repositories/propertyManagersRepository';
import type { MaintenanceRequestFilter } from '~/repositories/maintenanceRequestRepository';
import type {
  ConvertMaintenanceRequestToWorkOrderRequestDto,
  CreateMaintenanceRequestRequestDto,
  HydratedPropertyDto,
  HydratedPropertyManagerDto,
  HydratedUnitDto,
  MaintenanceRequestListItemDto,
  UpdateMaintenanceRequestRequestDto,
} from '~/swagger/maintenance/Api';
import type { MaintenanceRequestStatus, Urgency } from '~/swagger/portal/Api';

const repo = new MaintenanceRequestRepository();

export type CrossMaintenanceRequestListDto = MaintenanceRequestListItemDto & {
  unit?: HydratedUnitDto;
  property?: HydratedPropertyDto;
  propertyManager?: HydratedPropertyManagerDto;
};

export const [MaintenanceRequestsProvider, useMaintenanceRequests] = createMagicDoorContext('MaintenanceRequests', () => {
  const [maintenanceRequests, getMaintenanceRequests, maintenanceRequestsActions] = createTriggerResource(
    async (filter?: MaintenanceRequestFilter) => {
      const [res, propertyManagers] = await Promise.all([repo.getMaintenanceRequests(filter), getCachedPropertyManagers()]);
      const items: CrossMaintenanceRequestListDto[] = res.items.map((item) => ({
        ...item,
        unit: res?.units?.find((unit) => unit.id === item.unitId),
        property: res?.properties?.find((property) => property.id === item.propertyId),
        propertyManager: propertyManagers.find((propertyManager) => propertyManager.id === item.assignedPropertyManagerId),
      }));
      return {
        ...res,
        items,
      };
    }
  );
  const [maintenanceRequest, getMaintenanceRequest, maintenanceRequestActions] = createTriggerResource(
    repo.getMaintenanceRequest.bind(repo)
  );
  const [maintenanceRequestStats, maintenanceRequestStatsActions] = createLazyResource(repo.getMaintenanceRequestStats.bind(repo));

  const [categories, categoriesActions] = createLazyResource(
    maintenanceRequestCategoryRepository.getCategories.bind(maintenanceRequestCategoryRepository)
  );

  const setupDefaultCategories = createMutation(async () => {
    const createCategoryPromise = initialCategories.map(async (category) => {
      await maintenanceRequestCategoryRepository.createCategory({
        name: category.name,
        description: category.description,
      });
    });
    await Promise.allSettled(createCategoryPromise);
    toast.success('Default categories created');
    categoriesActions.refetch();
  });

  const addMaintenanceRequest = createMutation(async (payload: CreateMaintenanceRequestRequestDto) => {
    const response = await repo.addMaintenanceRequest(payload);
    maintenanceRequestsActions.refetch();
    maintenanceRequestStatsActions.refetch();
    return response;
  });

  const deleteMaintenanceRequest = createMutation(async (id: string) => {
    await repo.deleteMaintenanceRequest(id);
    id === maintenanceRequest()?.id && maintenanceRequestActions.mutate(undefined);
    maintenanceRequestsActions.refetch();
    maintenanceRequestStatsActions.refetch();
  });

  const updateMaintenanceRequest = createMutation(async (id: string, payload: UpdateMaintenanceRequestRequestDto) => {
    await repo.updateMaintenanceRequest(id, payload);
    id === maintenanceRequest()?.id && maintenanceRequestActions.refetch();
    maintenanceRequestsActions.refetch();
    maintenanceRequestStatsActions.refetch();
  });

  const updateMaintenanceRequestStatus = createMutation(async (id: string, status: MaintenanceRequestStatus) => {
    await repo.updateMaintenanceRequestStatus(id, status);
    id === maintenanceRequest()?.id && maintenanceRequestActions.refetch();
    maintenanceRequestsActions.refetch();
    maintenanceRequestStatsActions.refetch();
  });

  const updateMaintenanceRequestUrgency = createMutation(async (id: string, urgency: Urgency) => {
    await repo.updateMaintenanceRequestUrgency(id, urgency);
    id === maintenanceRequest()?.id && maintenanceRequestActions.refetch();
    maintenanceRequestsActions.refetch();
    maintenanceRequestStatsActions.refetch();
  });

  const disableMaintenanceRequestAi = createMutation(async () => {
    const maintenanceRequestId = maintenanceRequest()?.id;
    if (!maintenanceRequestId) return;
    await repo.disableMaintenanceRequestAi(maintenanceRequestId);
    maintenanceRequestActions.refetch();
  });

  const convertToWorkOrder = createMutation(async (id: string, payload: ConvertMaintenanceRequestToWorkOrderRequestDto) => {
    const response = await repo.convertToWorkOrder(id, payload);
    maintenanceRequestActions.refetch();
    return response;
  });

  return {
    maintenanceRequests,
    getMaintenanceRequests,
    maintenanceRequest,
    getMaintenanceRequest,
    get maintenanceRequestStats() {
      maintenanceRequestStatsActions.fetch();
      return maintenanceRequestStats;
    },
    addMaintenanceRequest,
    deleteMaintenanceRequest,
    updateMaintenanceRequest,
    updateMaintenanceRequestStatus,
    updateMaintenanceRequestUrgency,

    disableMaintenanceRequestAi,

    maintenanceRequestActions,
    convertToWorkOrder,

    categories,
    categoriesActions,

    setupDefaultCategories,
  };
});
